import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './contexts/AppContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProgressProvider } from './contexts/ProgressContext';

// Import base components
import ScoreScreen from './components/ScoreScreen';
import AnalysisScreen from './components/AnalysisScreen';
import Leaderboard from './components/Leaderboard';
import Register from './components/Register';
import Login from './components/Login';
import VerifyEmail from './components/VerifyEmail';
import VerificationPending from './components/VerificationPending';
import ProfileScreen from './components/ProfileScreen';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ChatScreen from './components/chat/ChatScreen';
import SwipeScreen from './components/SwipeScreen';
import MatchesScreen from './components/MatchesScreen';
import SettingsScreen from './components/SettingsScreen';
import AdminDashboard from './components/AdminDashboard';
import PaymentPlans from './components/PaymentPlans';
import PaymentSuccess from './components/PaymentSuccess';
import GuestOnboarding from './components/GuestOnboarding';
import TutorialOverlay from './components/TutorialOverlay';
import TestImageGeneration from './components/TestImageGeneration';

// Development logging
if (import.meta.env.DEV) {
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const [url, options] = args;

    console.group(`🌐 Fetch Request: ${options?.method || 'GET'} ${url}`);
    console.log('📤 Request options:', {
      ...options,
      headers: options?.headers
        ? Object.fromEntries(
            Object.entries(options.headers).map(([key, value]) => [
              key,
              key.toLowerCase() === 'authorization' ? '******' : value,
            ])
          )
        : {},
    });

    try {
      const startTime = performance.now();
      const response = await originalFetch(...args);
      const endTime = performance.now();
      const responseClone = response.clone();

      console.log(`⏱️ Response time: ${(endTime - startTime).toFixed(2)}ms`);
      console.log('📥 Response status:', response.status);
      console.log(
        '📥 Response headers:',
        Object.fromEntries(response.headers.entries())
      );

      try {
        const responseData = await responseClone.text();
        if (responseData) {
          try {
            console.log('📥 Response body:', JSON.parse(responseData));
          } catch {
            console.log('📥 Response body (text):', responseData);
          }
        }
      } catch (e) {
        console.log('⚠️ Could not read response body');
      }

      console.groupEnd();
      return response;
    } catch (error) {
      console.error('❌ Fetch error:', {
        message: error.message,
        stack: error.stack,
        type: error.constructor.name,
      });
      console.groupEnd();
      throw error;
    }
  };

  window.addEventListener('unhandledrejection', (event) => {
    console.error('🔥 Unhandled promise rejection:', {
      reason: event.reason,
      stack: event.reason?.stack,
      message: event.reason?.message,
    });
  });

  window.addEventListener('error', (event) => {
    console.error('💥 Global error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
    });
  });

  console.log('🔧 Development logging initialized');
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ProgressProvider>
          <AppProvider>
            <TutorialOverlay />
            <div className="ios-scroll-container max-w-md mx-auto min-h-screen bg-gradient-to-b from-indigo-600 via-purple-500 to-pink-500">
              <div className="safe-top safe-bottom safe-left safe-right">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/register" element={<Register />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/verification-pending" element={<VerificationPending />} />

                  {/* Protected Routes */}
                  <Route path="/profile" element={<ProtectedRoute component={ProfileScreen} />} />
                  <Route path="/leaderboard" element={<ProtectedRoute component={Leaderboard} />} />
                  <Route path="/settings" element={<ProtectedRoute component={SettingsScreen} />} />
                  
                  {/* Main App Routes */}
                  <Route path="/swipe" element={<ProtectedRoute component={SwipeScreen} />} />
                  <Route path="/matches" element={<ProtectedRoute component={MatchesScreen} />} />
                  <Route path="/chat" element={<ProtectedRoute component={ChatScreen} />} />
                  <Route path="/score" element={<ProtectedRoute component={ScoreScreen} />} />
                  <Route path="/analysis" element={<ProtectedRoute component={AnalysisScreen} />} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminRoute component={AdminDashboard} />} />

                  {/* Payment Routes */}
                  <Route path="/payment-plans" element={<ProtectedRoute component={PaymentPlans} />} />
                  <Route path="/payment-success" element={<ProtectedRoute component={PaymentSuccess} />} />

                  {/* Guest Onboarding Route */}
                  <Route path="/guest-onboarding" element={<GuestOnboarding />} />

                  {/* Test Image Generation Route */}
                  <Route path="/test-image" element={<TestImageGeneration />} />

                  {/* Root route redirects to SwipeScreen */}
                  <Route path="/" element={<Navigate to="/swipe" replace />} />
                </Routes>
              </div>
            </div>
          </AppProvider>
        </ProgressProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
